/**
 * Touch swipe helpers for carousels inside vertically scrollable parents (e.g. chat).
 */

const DEFAULT_TOUCH_LOCK_PX = 6;
const DEFAULT_SWIPE_THRESHOLD_PX = 40;
const CHAT_TOUCH_LOCK_PX = 4;
const CHAT_SWIPE_THRESHOLD_PX = 30;
const CHAT_HORIZONTAL_BIAS = 1.15;
/** Minimum horizontal movement before we block scroll / native taps (iOS needs this after vertical scroll). */
const HORIZONTAL_COMMIT_PX = 10;

/**
 * @param {HTMLElement} element
 * @param {{
 *   onSwipeLeft?: () => void,
 *   onSwipeRight?: () => void,
 *   isInteractiveTarget?: (target: EventTarget | null) => boolean,
 *   isSwipeAllowed?: () => boolean,
 *   useCapture?: boolean,
 *   chatMode?: boolean,
 * }} options
 * @returns {() => void} cleanup
 */
export function attachHorizontalSwipeGesture(element, options = {}) {
  const {
    onSwipeLeft,
    onSwipeRight,
    isInteractiveTarget = () => false,
    isSwipeAllowed = () => true,
    useCapture = false,
    chatMode = false,
  } = options;

  const touchLockPx = chatMode ? CHAT_TOUCH_LOCK_PX : DEFAULT_TOUCH_LOCK_PX;
  const swipeThresholdPx = chatMode ? CHAT_SWIPE_THRESHOLD_PX : DEFAULT_SWIPE_THRESHOLD_PX;
  const horizontalBias = chatMode ? CHAT_HORIZONTAL_BIAS : 1;

  const state = {
    active: false,
    dir: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
  };

  const resetState = () => {
    state.active = false;
    state.dir = null;
  };

  const handleSwipe = (startX, lastX) => {
    if (!isSwipeAllowed()) return;
    if (startX == null || lastX == null) return;
    const distance = startX - lastX;
    if (distance > swipeThresholdPx) {
      onSwipeLeft?.();
    } else if (distance < -swipeThresholdPx) {
      onSwipeRight?.();
    }
  };

  const resolveDirection = (absDx, absDy, cancelable) => {
    if (absDx < touchLockPx && absDy < touchLockPx) {
      return null;
    }

    if (chatMode) {
      if (absDx >= absDy * horizontalBias) {
        return 'horizontal';
      }
      if (absDy > absDx) {
        return 'vertical';
      }
      return cancelable ? 'horizontal' : 'vertical';
    }

    if (!cancelable) {
      return 'vertical';
    }
    return absDx > absDy ? 'horizontal' : 'vertical';
  };

  const onTouchStart = (e) => {
    if (!isSwipeAllowed()) {
      resetState();
      return;
    }

    if (!e.touches || e.touches.length !== 1) {
      resetState();
      return;
    }

    if (isInteractiveTarget(e.target)) {
      resetState();
      return;
    }

    const t = e.touches[0];
    state.active = true;
    state.dir = null;
    state.startX = t.clientX;
    state.startY = t.clientY;
    state.lastX = t.clientX;
    state.lastY = t.clientY;
  };

  const onTouchMove = (e) => {
    if (!state.active || !e.touches || e.touches.length !== 1) return;

    if (!isSwipeAllowed()) {
      resetState();
      return;
    }

    const t = e.touches[0];
    const dx = t.clientX - state.startX;
    const dy = t.clientY - state.startY;

    state.lastX = t.clientX;
    state.lastY = t.clientY;

    if (!state.dir) {
      const nextDir = resolveDirection(Math.abs(dx), Math.abs(dy), e.cancelable);
      if (!nextDir) return;
      state.dir = nextDir;
    }

    if (state.dir === 'horizontal') {
      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const committed =
        absDx >= HORIZONTAL_COMMIT_PX &&
        absDx > absDy * (chatMode ? horizontalBias : 1);

      // Defer preventDefault until the gesture is clearly horizontal so taps on
      // native video controls still work after scrolling the chat list (feed behavior).
      if (committed) {
        if (e.cancelable) {
          e.preventDefault();
        }
        e.stopPropagation();
      }
    }
  };

  const onTouchEnd = () => {
    if (!state.active) return;

    if (state.dir === 'horizontal' && isSwipeAllowed()) {
      handleSwipe(state.startX, state.lastX);
    }

    resetState();
  };

  const listenerOpts = useCapture ? { capture: true } : undefined;

  element.addEventListener('touchstart', onTouchStart, { passive: true, ...listenerOpts });
  element.addEventListener('touchmove', onTouchMove, { passive: false, ...listenerOpts });
  element.addEventListener('touchend', onTouchEnd, { passive: true, ...listenerOpts });
  element.addEventListener('touchcancel', onTouchEnd, { passive: true, ...listenerOpts });

  return () => {
    element.removeEventListener('touchstart', onTouchStart, listenerOpts);
    element.removeEventListener('touchmove', onTouchMove, listenerOpts);
    element.removeEventListener('touchend', onTouchEnd, listenerOpts);
    element.removeEventListener('touchcancel', onTouchEnd, listenerOpts);
  };
}
